import BigNumber from "bignumber.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Multicall } from "ethereum-multicall";
import { abi as erc20Abi } from "./web3/ERC20";
import { abi as hypeAbi } from "./web3/HyperbolicProtocol";
import { abi as uniV3Abi } from "./web3/IUniswapV3Pool";
import { abi as lendingPoolAbi } from "./web3/LendingPool";
import { abi as loanTokenAbi } from "./web3/LoanToken";
import { abi as rewardsAbi } from "./web3/LendingRewards";
import { processMulticallValue } from "./Utils";

dayjs.extend(utc);

export default {
  initCalls: [
    {
      refKey: "tokenInfo",
      stateKey: "tokenData",
      contractKey: "hype",
      abi: hypeAbi,
      calls: [
        { function: "name", params: [] },
        { function: "symbol", params: [] },
        { function: "decimals", params: [] },
        { function: "totalSupply", params: [] },
        { function: "lendingPool", params: [] },
        { function: "lendingRewards", params: [] },
        { function: "rewardsLocker", params: [] },
        { function: "twapUtils", params: [] },
        { function: "poolToMarketCapTarget", params: [] },
        { function: "taxesEnabled", params: [] },
        { function: "taxesOnTransfers", params: [] },
        { function: "taxesOnBuys", params: [] },
        { function: "taxesOnSells", params: [] },
        { function: "minTax", params: [] },
        { function: "maxTax", params: [] },
        { function: "lpTax", params: [] },
        { function: "swapAtAmount", params: [] },
        { function: "swapEnabled", params: [] },
        {
          function: "calculateTaxFromAmount",
          params: [
            new BigNumber(100)
              .times(new BigNumber(10).pow(18))
              .toFixed(0),
          ],
        },
        { function: "poolBalToMarketCapRatio", params: [] },
      ],
    },
    {
      refKey: "hlpInfo",
      stateKey: "hlpData",
      contractKey: "hlp",
      abi: erc20Abi,
      calls: [
        { function: "name", params: [] },
        { function: "symbol", params: [] },
        { function: "decimals", params: [] },
        { function: "totalSupply", params: [] },
      ],
    },
  ],

  initCalls2: [
    {
      refKey: "nativeStableInfo",
      stateKey: "nativeStableData",
      contractKey: "nativeStablePool",
      abi: uniV3Abi,
      calls: [
        { function: "token0", params: [] },
        { function: "token1", params: [] },
        { function: "slot0", params: [] },
      ],
    },
    {
      refKey: "tokenLPInfo",
      stateKey: "tokenLPData",
      contractKey: "mainLp",
      abi: uniV3Abi,
      calls: [
        { function: "token0", params: [] },
        { function: "token1", params: [] },
        { function: "slot0", params: [] },
      ],
    },
    {
      refKey: "lendingPoolInfo",
      stateKey: "lendingPoolData",
      contractKey: "lendingPool",
      abi: lendingPoolAbi,
      calls: [
        { function: "enabled", params: [] },
        { function: "loanNFT", params: [] },
        { function: "borrowInitFee", params: [] },
        { function: "borrowAPRMin", params: [] },
        { function: "borrowAPRMax", params: [] },
        { function: "maxLTVOverall", params: [] },
        { function: "liquidationLTV", params: [] },
        { function: "getAllWhitelistedPools", params: [] },
        { function: "calculateBorrowAPR", params: [] },
      ],
    },
    {
      refKey: "lendingPoolExtInfo",
      stateKey: "lendingPoolExtData",
      contractKey: "lendingPoolExt",
      abi: lendingPoolAbi,
      calls: [
        { function: "enabled", params: [] },
        { function: "loanNFT", params: [] },
        { function: "borrowInitFee", params: [] },
        { function: "borrowAPRMin", params: [] },
        { function: "borrowAPRMax", params: [] },
        { function: "maxLTVOverall", params: [] },
        { function: "liquidationLTV", params: [] },
        { function: "getAllWhitelistedPools", params: [] },
        { function: "calculateBorrowAPR", params: [] },
      ],
    },
    {
      refKey: "loanTokenInfo",
      stateKey: "loanTokenData",
      contractKey: "loanToken",
      abi: loanTokenAbi,
      calls: [{ function: "getAllOpenedLoans", params: [] }],
    },
    {
      refKey: "loanTokenExtInfo",
      stateKey: "loanTokenExtData",
      contractKey: "loanTokenExt",
      abi: loanTokenAbi,
      calls: [{ function: "getAllOpenedLoans", params: [] }],
    },
    {
      refKey: "rewardsInfo",
      stateKey: "rewardsData",
      contractKey: "lendingRewards",
      abi: rewardsAbi,
      calls: [
        { function: "totalSharesDeposited", params: [] },
        { function: "totalStakedUsers", params: [] },
        { function: "totalRewards", params: [] },
        { function: "totalDistributed", params: [] },
        ...new Array(12).fill(0).map((_, _i) => {
          const month = dayjs()
            .utc()
            .subtract(_i, "months")
            .startOf("month")
            .unix();
          return {
            function: "monthlyRewards",
            stateKey: `monthlyFees_${month}`,
            params: [month],
          };
        }),
      ],
    },
    {
      refKey: "rewardsExtInfo",
      stateKey: "rewardsExtData",
      contractKey: "lendingRewardsExt",
      abi: rewardsAbi,
      calls: [
        { function: "totalSharesDeposited", params: [] },
        { function: "totalStakedUsers", params: [] },
        { function: "totalRewards", params: [] },
        { function: "totalDistributed", params: [] },
        ...new Array(12).fill(0).map((_, _i) => {
          const month = dayjs()
            .utc()
            .subtract(_i, "months")
            .startOf("month")
            .unix();
          return {
            function: "monthlyRewards",
            stateKey: `monthlyFees_${month}`,
            params: [month],
          };
        }),
      ],
    },
  ],

  loanCalls(
    collateralPools,
    activeLoansMain,
    activeLoansExt,
    lendingPoolContract,
    lendingPoolExtContract
  ) {
    return [
      ...buildCollateralPoolDetailCalls(collateralPools),
      {
        refKey: "collPoolMaxInfo",
        stateKey: "collPoolMaxInfo",
        contractKey: "lendingPool",
        abi: lendingPoolAbi,
        calls: collateralPools
          .map((pool) => [
            {
              function: "maxLTVOverride",
              stateKey: `maxLTVOverride_${pool.toLowerCase()}`,
              params: [pool],
            },
          ])
          .flat(1),
      },
      {
        refKey: "collPoolExtMaxInfo",
        stateKey: "collPoolExtMaxInfo",
        contractKey: "lendingPoolExt",
        abi: lendingPoolAbi,
        calls: collateralPools
          .map((pool) => [
            {
              function: "maxLTVOverride",
              stateKey: `maxLTVOverride_${pool.toLowerCase()}`,
              params: [pool],
            },
            {
              function: "currentBorrowPerPool",
              stateKey: `currentBorrowPerPool_${pool.toLowerCase()}`,
              params: [pool],
            },
            {
              function: "maxBorrowPerPool",
              stateKey: `maxBorrowPerPool_${pool.toLowerCase()}`,
              params: [pool],
            },
          ])
          .flat(1),
      },
      {
        refKey: "loanSecondaryInfo",
        stateKey: "loanSecondaryInfo",
        contractKey: "lendingPool",
        abi: lendingPoolAbi,
        calls: activeLoansMain
          .map((loanId) => [
            {
              function: "loans",
              stateKey: `loan_${loanId}`,
              params: [loanId],
            },
            {
              function: "getLTVX96",
              stateKey: `ltvX96_${loanId}`,
              params: [loanId],
            },
          ])
          .flat(1),
      },
      {
        refKey: "loanSecondaryExtInfo",
        stateKey: "loanSecondaryExtInfo",
        contractKey: "lendingPoolExt",
        abi: lendingPoolAbi,
        calls: activeLoansExt
          .map((loanId) => [
            {
              function: "loans",
              stateKey: `loan_${loanId}`,
              params: [loanId],
            },
            {
              function: "getLTVX96",
              stateKey: `ltvX96_${loanId}`,
              params: [loanId],
            },
          ])
          .flat(1),
      },
      {
        refKey: "lendingPoolETHBal",
        stateKey: "lendingPoolETHBal",
        contractKey: "lendingPool",
        abi: lendingPoolAbi,
        calls: [
          {
            function: "getETHBalance",
            stateKey: `balance`,
            params: [lendingPoolContract],
          },
        ],
      },
      {
        refKey: "lendingPoolExtETHBal",
        stateKey: "lendingPoolExtETHBal",
        contractKey: "lendingPool",
        abi: lendingPoolAbi,
        calls: [
          {
            function: "getETHBalance",
            stateKey: `balance`,
            params: [lendingPoolExtContract],
          },
        ],
      },
      {
        refKey: "loanOwnerInfo",
        stateKey: "loanOwnerInfo",
        contractKey: "loanToken",
        abi: loanTokenAbi,
        calls: activeLoansMain.map((loanId) => ({
          function: "ownerOf",
          stateKey: `ownerOf_${loanId}`,
          params: [loanId],
        })),
      },
      {
        refKey: "loanOwnerExtInfo",
        stateKey: "loanOwnerExtInfo",
        contractKey: "loanTokenExt",
        abi: loanTokenAbi,
        calls: activeLoansExt.map((loanId) => ({
          function: "ownerOf",
          stateKey: `ownerOf_${loanId}`,
          params: [loanId],
        })),
      },
    ];
  },

  poolCalls(pools) {
    return buildCollateralPoolDetailCalls(
      pools,
      "secCollateralPoolInfo_"
    );
  },

  tokenDetailCalls(tokens) {
    return tokens.map((tokenAddy) => ({
      refKey: `tokenInfo_${tokenAddy.toLowerCase()}`,
      stateKey: `tokenInfo_${tokenAddy.toLowerCase()}`,
      contractAddress: tokenAddy,
      abi: erc20Abi,
      calls: [
        { function: "name", params: [] },
        { function: "symbol", params: [] },
        { function: "decimals", params: [] },
      ],
    }));
  },

  userCalls(userAddy, tokensForBalances) {
    return [
      {
        refKey: "userTokenInfo",
        stateKey: "userTokenData",
        contractKey: "hype",
        abi: hypeAbi,
        calls: [
          {
            function: "balanceOf",
            stateKey: "balance",
            params: [userAddy],
          },
        ],
      },
      {
        refKey: "userHLPInfo",
        stateKey: "userHLPData",
        contractKey: "hlp",
        abi: erc20Abi,
        calls: [
          {
            function: "balanceOf",
            stateKey: "balance",
            params: [userAddy],
          },
        ],
      },
      {
        refKey: "userRewardsInfo",
        stateKey: "userRewardsData",
        contractKey: "lendingRewards",
        abi: rewardsAbi,
        calls: [
          {
            function: "getUnpaid",
            stateKey: "userUnpaid",
            params: [userAddy],
          },
          {
            function: "rewards",
            stateKey: "rewards",
            params: [userAddy],
          },
          {
            function: "getShares",
            stateKey: "userShares",
            params: [userAddy],
          },
        ],
      },
      {
        refKey: "userRewardsExtInfo",
        stateKey: "userRewardsExtData",
        contractKey: "lendingRewardsExt",
        abi: rewardsAbi,
        calls: [
          {
            function: "getUnpaid",
            stateKey: "userUnpaid",
            params: [userAddy],
          },
          {
            function: "rewards",
            stateKey: "rewards",
            params: [userAddy],
          },
          {
            function: "getShares",
            stateKey: "userShares",
            params: [userAddy],
          },
        ],
      },
      {
        refKey: "userETHBal",
        stateKey: "userETHBal",
        contractKey: "lendingPool",
        abi: lendingPoolAbi,
        calls: [
          {
            function: "getETHBalance",
            stateKey: `balance`,
            params: [userAddy],
          },
        ],
      },
      ...tokensForBalances.reduce((ary, token) => {
        return [
          ...ary,
          {
            refKey: `tokenUserBal_${token.toLowerCase()}`,
            stateKey: `tokenUserBal_${token.toLowerCase()}`,
            contractAddress: token,
            abi: erc20Abi,
            calls: [{ function: "balanceOf", params: [userAddy] }],
          },
        ];
      }, []),
    ];
  },
};

export async function executeMulticall(web3, callsAry, contracts) {
  const multicall = new Multicall({
    web3Instance: web3,
    tryAggregate: true,
  });
  const context = callsAry
    .map((contractInfo) => {
      const contractAddress = contractInfo.contractAddress
        ? contractInfo.contractAddress
        : contracts[contractInfo.contractKey];
      // short circuit if contract not available and filter below
      if (!contractAddress) {
        return;
      }
      return {
        reference: contractInfo.refKey,
        contractAddress,
        abi: contractInfo.abi,
        calls: contractInfo.calls.map((call) => ({
          reference: contractInfo.refKey,
          methodName: call.function,
          methodParameters: call.params,
        })),
      };
    })
    .filter((ctx) => !!ctx);
  const { results } = await multicall.call(context);
  return results;
}

export function processMulticallCallsReturnContext(
  results,
  callsAry,
  callback
) {
  return callsAry.forEach((info, idx) => {
    const refKey = info.refKey;
    callback({
      idx,
      key: info.stateKey,
      obj:
        (results[refKey] &&
          results[refKey].callsReturnContext.reduce(
            (obj, resContext, callInd) => {
              const objKey =
                info.calls[callInd].stateKey || resContext.methodName;
              return {
                ...obj,
                // TODO: better handle multiple return values, putting them into a struct/obj, etc.
                [objKey]:
                  resContext.returnValues.length === 1
                    ? processMulticallValue(
                        resContext.returnValues[0]
                      )
                    : resContext.returnValues.map((v) =>
                        processMulticallValue(v)
                      ),
              };
            },
            {}
          )) ||
        null,
    });
  });
}

function buildCollateralPoolDetailCalls(
  pools,
  stateKeyPrefix = "collateralPoolInfo_"
) {
  return [
    ...pools.reduce((ary, poolAddress) => {
      return [
        ...ary,
        {
          refKey: `collPoolInfo_${poolAddress.toLowerCase()}`,
          stateKey: `${stateKeyPrefix}${poolAddress.toLowerCase()}`,
          contractAddress: poolAddress,
          abi: uniV3Abi,
          calls: [
            { function: "token0", params: [] },
            { function: "token1", params: [] },
            { function: "slot0", params: [] },
          ],
        },
      ];
    }, []),
  ];
}
